import { CREATED_BY } from "@sajilni/config/constants";
import {
  prisma,
  type Prisma,
  OrderStatus,
  OrderItemStatus,
  OrderType,
  PaymentStatus,
  TransitionalModel,
  EntityStatus,
} from "client";
import {
  createOrderTransition,
  createOrderItemTransition,
} from "services/Transitions";
import { updatePayment } from "services/payment";
import { releaseSeats } from "services/seatingBuilder/seat-override";

/**
 * Give back whatever an order was holding: booth/seat-less entities go back to
 * AVAILABLE, derived seats release by deleting their override row.
 */
export const releaseOrdersInventory = async (orderIds: string[]) => {
  if (orderIds.length === 0) return;

  const entityOrderItems = await prisma.orderItem.findMany({
    where: { orderId: { in: orderIds }, entityId: { not: null } },
    select: { entityId: true, seatSectionId: true, row: true, seat: true },
  });

  const entityIds = entityOrderItems
    .filter((item) => item.seatSectionId === null)
    .map((item) => item.entityId)
    .filter((id): id is string => id !== null);

  const seats = entityOrderItems
    .filter(
      (item) =>
        item.seatSectionId !== null && item.row !== null && item.seat !== null
    )
    .map((item) => ({
      seatSectionId: item.seatSectionId!,
      row: item.row!,
      seat: item.seat!,
    }));

  await Promise.all([
    entityIds.length > 0
      ? prisma.entity.updateMany({
          where: { id: { in: entityIds } },
          data: {
            status: EntityStatus.AVAILABLE,
            exhibitorId: null,
            invitationAttendeeId: null,
            sponsorId: null,
          },
        })
      : null,
    releaseSeats(seats),
  ]);
};

const expireAwaitingPaymentsForOrders = async (orderIds: string[]) => {
  if (orderIds.length === 0) return 0;

  const payments = await prisma.payment.findMany({
    where: {
      orderId: { in: orderIds },
      status: PaymentStatus.AWAITING_PAYMENT,
    },
    select: { id: true, status: true },
  });

  if (payments.length === 0) return 0;

  const paymentIds = payments.map((payment) => payment.id);

  await prisma.payment.updateMany({
    where: { id: { in: paymentIds } },
    data: { status: PaymentStatus.EXPIRED },
  });

  await prisma.transition.updateMany({
    where: {
      transitionalId: { in: paymentIds },
      transitionalType: TransitionalModel.PAYMENT,
      mostRecent: true,
    },
    data: { mostRecent: false },
  });

  await prisma.transition.createMany({
    data: payments.map((payment) => ({
      transitionalId: payment.id,
      transitionalType: TransitionalModel.PAYMENT,
      mostRecent: true,
      createdBy: CREATED_BY.CRON,
      fromState: payment.status,
      toState: PaymentStatus.EXPIRED,
    })),
  });

  return payments.length;
};

const expiredOrderSelect = {
  id: true,
  total: true,
  subTotal: true,
  type: true,
  payments: {
    select: {
      id: true,
      status: true,
      paymentMethodId: true,
      gatewayResponse: true,
      gatewayReferenceId: true,
      grandTotal: true,
    },
  },
} satisfies Prisma.OrderSelect;

export const getExpiredOrders = async () => {
  const currentDate = new Date();
  const openStatuses = [OrderStatus.NEW, OrderStatus.ON_GOING] as const;
  // Cap per cron tick so a backlog can't run forever and keep db-transactional open.
  const take = 500;

  // Completed orders with only expired AWAITING_PAYMENT rows (e.g. bank transfer).
  // Require `some` as well as `every`: Prisma's `every` is vacuously true when
  // there are no payments, which incorrectly matched free completed orders.
  const expiredAwaitingPaymentFilter = {
    status: PaymentStatus.AWAITING_PAYMENT,
    expiresAt: { not: null, lt: currentDate },
  } as const;
  const completedUnpaidExpired = {
    status: OrderStatus.COMPLETED,
    payments: {
      some: expiredAwaitingPaymentFilter,
      every: expiredAwaitingPaymentFilter,
    },
  } satisfies Prisma.OrderWhereInput;

  // Ticket/entity orders expire against their own `order.expiryDate`.
  // Invitation orders expire when the parent invitation's fixed `expiryDate`
  // passes, regardless of what is stored on the order row.
  const [standardExpiredOrders, invitationExpiredRaw] = await Promise.all([
    prisma.order.findMany({
      where: {
        AND: [
          {
            type: { not: OrderType.INVITATION },
            expiryDate: { lt: currentDate },
          },
          {
            OR: [
              { status: { in: [...openStatuses] } },
              completedUnpaidExpired,
            ],
          },
        ],
      },
      select: expiredOrderSelect,
      take,
      orderBy: { expiryDate: "asc" },
    }),
    prisma.order.findMany({
      where: {
        type: OrderType.INVITATION,
        OR: [
          { status: { in: [...openStatuses] } },
          completedUnpaidExpired,
        ],
        orderItems: {
          some: {
            invitationAttendee: {
              invitation: {
                expiryDate: { lt: currentDate, not: null },
              },
            },
          },
        },
      },
      // Over-fetch then sort by invitation.expiryDate — order.createdAt is the wrong clock.
      take: take * 4,
      select: {
        ...expiredOrderSelect,
        orderItems: {
          where: { invitationAttendeeId: { not: null } },
          take: 1,
          select: {
            invitationAttendee: {
              select: {
                invitation: { select: { expiryDate: true } },
              },
            },
          },
        },
      },
    }),
  ]);

  const invitationExpiredOrders = invitationExpiredRaw
    .map((order) => {
      const invitationExpiry =
        order.orderItems[0]?.invitationAttendee?.invitation?.expiryDate ??
        null;
      const { orderItems: _omit, ...rest } = order;
      return { order: rest, invitationExpiry };
    })
    .sort((a, b) => {
      const aTime = a.invitationExpiry?.getTime() ?? Number.POSITIVE_INFINITY;
      const bTime = b.invitationExpiry?.getTime() ?? Number.POSITIVE_INFINITY;
      return aTime - bTime;
    })
    .slice(0, take)
    .map(({ order }) => order);

  return [...standardExpiredOrders, ...invitationExpiredOrders];
};

export const expireOrder = async (expiredOrderId: string) => {
  await prisma.order.update({
    where: {
      id: expiredOrderId,
    },
    data: {
      status: OrderStatus.EXPIRED,
      orderItems: {
        updateMany: {
          where: {},
          data: {
            status: OrderItemStatus.EXPIRED,
          },
        },
      },
    },
  });
  await createOrderTransition({
    status: OrderStatus.EXPIRED,
    orderId: expiredOrderId,
    createdBy: CREATED_BY.CRON,
  });
  const orderItems = await prisma.orderItem.findMany({
    where: {
      orderId: expiredOrderId,
    },
    select: {
      id: true,
    },
  });
  ///CREATE MANY
  const orderItemTransitions = orderItems.map((item) =>
    createOrderItemTransition({
      status: OrderItemStatus.EXPIRED,
      orderItemId: item.id,
      createdBy: CREATED_BY.CRON,
    })
  );
  // Execute all transitions concurrently
  await Promise.all(orderItemTransitions);

  const awaitingPayments = await prisma.payment.findMany({
    where: {
      orderId: expiredOrderId,
      status: PaymentStatus.AWAITING_PAYMENT,
    },
    select: { id: true },
  });

  await Promise.all(
    awaitingPayments.map((payment) =>
      updatePayment(
        payment.id,
        { status: PaymentStatus.EXPIRED },
        { createdBy: CREATED_BY.CRON }
      )
    )
  );

  await releaseOrdersInventory([expiredOrderId]);
};

export const expireOrders = async (expiredOrderIds: string[]) => {
  await prisma.order.updateMany({
    where: { id: { in: expiredOrderIds } },
    data: {
      status: OrderStatus.EXPIRED,
    },
  });
  await prisma.transition.updateMany({
    where: {
      transitionalId: { in: expiredOrderIds },
      transitionalType: TransitionalModel.ORDER,
      mostRecent: true,
    },
    data: {
      mostRecent: false,
    },
  });
  await prisma.transition.createMany({
    data: expiredOrderIds.map((id) => ({
      transitionalId: id,
      transitionalType: TransitionalModel.ORDER,
      mostRecent: true,
      createdBy: CREATED_BY.CRON,
      fromState: OrderStatus.ON_GOING, // Represents the last known valid state before expiry. Typically, an order is in a ON_GOING state before it transitions to EXPIRED.
      toState: OrderStatus.EXPIRED,
    })),
  });
  await prisma.orderItem.updateMany({
    where: { orderId: { in: expiredOrderIds } },
    data: {
      status: OrderItemStatus.EXPIRED,
    },
  });
  const orderItems = await prisma.orderItem.findMany({
    where: { orderId: { in: expiredOrderIds } },
    select: { id: true },
  });
  const orderItemIds = orderItems.map((item) => item.id);
  await prisma.transition.updateMany({
    where: {
      transitionalId: { in: orderItemIds },
      transitionalType: TransitionalModel.ORDER_ITEM,
      mostRecent: true,
    },
    data: {
      mostRecent: false,
    },
  });

  await prisma.transition.createMany({
    data: orderItemIds.map((id) => ({
      transitionalId: id,
      transitionalType: TransitionalModel.ORDER_ITEM,
      mostRecent: true,
      createdBy: CREATED_BY.CRON,
      fromState: OrderItemStatus.RESERVED, // Represents the last known valid state before expiry. Typically, an order item is in a RESERVED state before it transitions to EXPIRED.
      toState: OrderItemStatus.EXPIRED,
    })),
  });

  await releaseOrdersInventory(expiredOrderIds);

  await expireAwaitingPaymentsForOrders(expiredOrderIds);
};
